import {
  Bookmark,
  BookmarkCheck,
  Check,
  ChevronLeft,
  Copy,
  ExternalLink,
  FileText,
  Gift,
  IdCard,
  Loader2,
  Search,
  Tag,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

interface MemberBenefitsScreenProps {
  partyId: string
}

interface Benefit {
  id: number
  name: string
  logoUrl: string | null
  headline: string
  description: string | null
  redemptionType: string // Flyer | MemberID | StaticCode | LinkOnly
  voucherCode: string | null
  flyerUrl: string | null
  websiteUrl: string | null
  howToSteps: string | null // JSON array string
  attachmentsJson: string | null // JSON array of {label, url}
  barcodeUrl: string | null
  isActive: boolean
  sortOrder: number
}

function parseSteps(raw: string | null): string[] {
  if (!raw) return []
  try {
    return JSON.parse(raw) as string[]
  } catch {
    return []
  }
}

function parseAttachments(raw: string | null): { label: string; url: string }[] {
  if (!raw) return []
  try {
    return JSON.parse(raw) as { label: string; url: string }[]
  } catch {
    return []
  }
}

function RedemptionTypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; className: string }> = {
    Flyer: { label: 'Flyer', className: 'bg-blue-100 text-blue-700' },
    MemberID: { label: 'Member ID', className: 'bg-purple-100 text-purple-700' },
    StaticCode: { label: 'Code', className: 'bg-green-100 text-green-700' },
    LinkOnly: { label: 'Link', className: 'bg-amber-100 text-amber-700' },
  }
  const { label, className } = map[type] ?? {
    label: type,
    className: 'bg-secondary text-secondary-foreground',
  }
  return (
    <span className={`text-3xs font-medium px-2 py-0.5 rounded-full ${className}`}>{label}</span>
  )
}

function BenefitDetail({
  benefit,
  partyId,
  onBack,
}: {
  benefit: Benefit
  partyId: string
  onBack: () => void
}) {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)
  const steps = parseSteps(benefit.howToSteps)
  const displayCode = benefit.redemptionType === 'MemberID' ? partyId : benefit.voucherCode

  const handleCopy = () => {
    if (!displayCode) return
    navigator.clipboard.writeText(displayCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast({ title: 'Copied!', description: 'Code copied to clipboard.' })
      trackEvent('benefit_voucher_copied', { benefit: benefit.name, type: benefit.redemptionType })
    })
  }

  return (
    <div className="flex flex-col min-h-full pb-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-primary mb-4 hover:underline"
      >
        <ChevronLeft className="w-3 h-3" />
        Back to Offers
      </button>

      {/* Hero */}
      <div className="bg-gradient-reinz px-4 py-6 -mx-4 mb-6 rounded-b-2xl">
        <div className="flex items-center gap-3 mb-1">
          {benefit.logoUrl ? (
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={benefit.logoUrl}
                alt={benefit.name}
                className="w-full h-full object-contain p-1"
              />
            </div>
          ) : (
            <Gift className="w-5 h-5 text-primary-foreground" />
          )}
          <h1 className="text-xl font-semibold text-primary-foreground">{benefit.name}</h1>
        </div>
        <p className="text-primary-foreground/80 text-sm">{benefit.headline}</p>
      </div>

      {/* Description */}
      {benefit.description && (
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed whitespace-pre-line">
          {benefit.description.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
            /^https?:\/\//.test(part) ? (
              <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline break-all"
              >
                {part}
              </a>
            ) : (
              part
            ),
          )}
        </p>
      )}

      {/* Code / ID display */}
      {(benefit.redemptionType === 'StaticCode' || benefit.redemptionType === 'MemberID') &&
        displayCode && (
          <Card className="p-4 mb-5">
            <p className="text-xs text-muted-foreground mb-2">
              {benefit.redemptionType === 'MemberID' ? 'Your REINZ Member ID' : 'Voucher Code'}
            </p>
            <div className="flex items-center justify-between gap-3 bg-muted rounded-lg px-4 py-3">
              <span className="text-lg font-mono font-bold text-foreground tracking-widest">
                {displayCode}
              </span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs text-primary font-medium"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" /> Copy
                  </>
                )}
              </button>
            </div>
          </Card>
        )}

      {/* Flyer */}
      {benefit.redemptionType === 'Flyer' && (
        <Card className="p-4 mb-5 flex flex-col items-center gap-3 text-center">
          <FileText className="w-10 h-10 text-primary/60" />
          <p className="text-sm text-muted-foreground">
            Show this flyer at the counter to redeem your discount.
          </p>
          {benefit.flyerUrl ? (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => window.open(benefit.flyerUrl!, '_blank')}
            >
              <FileText className="w-4 h-4" />
              Open Member Flyer
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground italic">Flyer will be available soon.</p>
          )}
        </Card>
      )}

      {/* Barcode */}
      {benefit.barcodeUrl && (
        <Card className="p-4 mb-5 flex flex-col items-center gap-2">
          <p className="text-xs text-muted-foreground self-start">Member Barcode</p>
          <img
            src={benefit.barcodeUrl}
            alt="Member barcode"
            className="w-full max-w-xs rounded-lg object-contain"
          />
        </Card>
      )}

      {/* How to steps */}
      {steps.length > 0 && (
        <section className="mb-5">
          <h2 className="text-sm font-semibold text-foreground mb-3">How to Redeem</h2>
          <div className="space-y-2">
            {steps.map((step, i) => {
              // Auto-link email addresses in step text
              const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g
              const parts = step.split(emailRegex)
              return (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-foreground leading-snug">
                    {parts.map((part, j) =>
                      emailRegex.test(part) ? (
                        <a key={j} href={`mailto:${part}`} className="text-primary underline">
                          {part}
                        </a>
                      ) : (
                        part
                      ),
                    )}
                  </p>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* PDF / attachment buttons */}
      {parseAttachments(benefit.attachmentsJson).length > 0 && (
        <div className="space-y-3">
          {parseAttachments(benefit.attachmentsJson).map((a, i) => (
            <Button
              key={i}
              variant="outline"
              className="w-full gap-2"
              onClick={() => window.open(a.url, '_blank')}
            >
              <FileText className="w-4 h-4" />
              {a.label}
            </Button>
          ))}
        </div>
      )}

      {/* Website link */}
      {benefit.websiteUrl && (
        <>
          {(benefit.redemptionType === 'MemberID' || benefit.redemptionType === 'StaticCode') &&
            (benefit.redemptionType === 'MemberID' ? partyId : benefit.voucherCode) && (
              <p className="text-xs text-muted-foreground text-center mt-3 -mb-1">
                Copy your code above, then visit the website to apply it.
              </p>
            )}
          <Button
            className="w-full gap-2 mt-3"
            onClick={() => {
              trackEvent('benefit_website_clicked', { benefit: benefit.name })
              window.open(benefit.websiteUrl!, '_blank')
            }}
          >
            <ExternalLink className="w-4 h-4" />
            Visit {benefit.name} Website
          </Button>
        </>
      )}
    </div>
  )
}

const PREF_KEY = 'benefit_preferences'

export function MemberBenefitsScreen({ partyId }: MemberBenefitsScreenProps) {
  const banner = useBanner('memberperks')
  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'member_benefits' })
  }, [])
  const { toast } = useToast()
  const [benefits, setBenefits] = useState<Benefit[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Benefit | null>(null)
  const [search, setSearch] = useState('')
  const [showMyBenefits, setShowMyBenefits] = useState(false)
  const [preferredIds, setPreferredIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    fetch('/api/memberbenefits')
      .then((r) => r.json())
      .then(setBenefits)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!partyId) return
    fetch(`/api/user-preferences/${PREF_KEY}?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.value) {
          const ids: number[] = JSON.parse(data.value)
          setPreferredIds(new Set(ids))
        }
      })
      .catch(() => {})
  }, [partyId])

  const togglePreference = useCallback(
    async (e: React.MouseEvent, benefitId: number) => {
      e.stopPropagation()
      const next = new Set(preferredIds)
      if (next.has(benefitId)) {
        next.delete(benefitId)
      } else {
        next.add(benefitId)
      }
      setPreferredIds(next)

      try {
        await fetch('/api/user-preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: partyId,
            preferenceKey: PREF_KEY,
            preferenceValue: JSON.stringify([...next]),
          }),
        })
      } catch {
        toast({ title: 'Could not save preference', variant: 'destructive' })
      }
    },
    [partyId, preferredIds, toast],
  )

  const scrollToTop = () => {
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'instant' })
  }

  if (selected) {
    return (
      <BenefitDetail
        benefit={selected}
        partyId={partyId}
        onBack={() => {
          setSelected(null)
          scrollToTop()
        }}
      />
    )
  }

  const typeIcon: Record<string, React.ReactNode> = {
    Flyer: <FileText className="w-5 h-5 text-blue-500" />,
    MemberID: <IdCard className="w-5 h-5 text-purple-500" />,
    StaticCode: <Tag className="w-5 h-5 text-green-500" />,
    LinkOnly: <ExternalLink className="w-5 h-5 text-amber-500" />,
  }

  const filtered = benefits.filter((b) => {
    const matchesSearch =
      !search ||
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.headline.toLowerCase().includes(search.toLowerCase())
    const matchesPref = !showMyBenefits || preferredIds.has(b.id)
    return matchesSearch && matchesPref
  })

  return (
    <div className="flex flex-col min-h-full pb-4">
      <ServiceBanner banner={banner} />
      {/* Hero */}
      <div className="bg-gradient-reinz px-4 py-6 -mx-4 mb-6 rounded-b-2xl">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="w-6 h-6 text-primary-foreground" />
          <h1 className="text-xl font-semibold text-primary-foreground font-heading">
            Partner Offers for REINZ Members
          </h1>
        </div>
        <p className="text-primary-foreground/80 text-sm">Exclusive Offers for REINZ members</p>
      </div>

      {!loading && benefits.length > 0 && (
        <>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search benefits..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9 h-8 text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowMyBenefits(false)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                !showMyBenefits
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              All Offers
            </button>
            <button
              onClick={() => setShowMyBenefits(true)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                showMyBenefits
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <BookmarkCheck className="w-3.5 h-3.5" />
              My Offers
              {preferredIds.size > 0 && (
                <span
                  className={`text-3xs rounded-full px-1.5 py-0.5 font-bold ${
                    showMyBenefits ? 'bg-primary-foreground/20' : 'bg-primary/10 text-primary'
                  }`}
                >
                  {preferredIds.size}
                </span>
              )}
            </button>
          </div>
        </>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : benefits.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-16">
          No benefits available yet.
        </p>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-2">
          {showMyBenefits ? (
            <>
              <Bookmark className="w-8 h-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground text-center">No saved benefits yet.</p>
              <p className="text-xs text-muted-foreground text-center">
                Tap the bookmark icon on any benefit to save it here.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              No benefits match "{search}"
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <Card
              key={b.id}
              className="p-4 cursor-pointer hover:bg-muted/40 transition-colors active:scale-[0.98]"
              onClick={() => {
                setSelected(b)
                scrollToTop()
                trackEvent('benefit_opened', { benefit: b.name })
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded-lg shrink-0 overflow-hidden">
                  {b.logoUrl ? (
                    <img src={b.logoUrl} alt={b.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full bg-secondary flex items-center justify-center">
                      {typeIcon[b.redemptionType] ?? (
                        <Gift className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="font-medium text-foreground text-sm">{b.name}</p>
                    <RedemptionTypeBadge type={b.redemptionType} />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{b.headline}</p>
                </div>
                <button
                  onClick={(e) => togglePreference(e, b.id)}
                  className="shrink-0 mt-0.5 p-1 -mr-1 text-muted-foreground hover:text-primary transition-colors"
                  aria-label={
                    preferredIds.has(b.id) ? 'Remove from My Offers' : 'Save to My Offers'
                  }
                >
                  {preferredIds.has(b.id) ? (
                    <BookmarkCheck className="w-4 h-4 text-primary" />
                  ) : (
                    <Bookmark className="w-4 h-4" />
                  )}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
